import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Button,
  Card,
  Col,
  DatePicker,
  Descriptions,
  Divider,
  Drawer,
  Empty,
  Form,
  Input,
  InputNumber,
  Modal,
  Row,
  Select,
  Space,
  Statistic,
  Table,
  Tag,
  Typography,
  message,
} from "antd";
import {
  FilterOutlined,
  PlusOutlined,
  ReloadOutlined,
  SearchOutlined,
} from "@ant-design/icons";
import axios from "axios";
import dayjs, { Dayjs } from "dayjs";
import type { ColumnsType } from "antd/es/table";
import type { RangePickerProps } from "antd/es/date-picker";
import API_CONFIG from "../../config/api";
import { useSelection } from "../../SelectionContext";
import { useAuth } from "../../context/AuthContext";

const { RangePicker } = DatePicker;
const { Text } = Typography;

type ReservationStatus =
  | "pending"
  | "confirmed"
  | "checked_in"
  | "checked_out"
  | "cancelled"
  | "no_show";
type PaymentStatus = "unpaid" | "partial" | "paid" | "refunded";
type ReservationSource =
  | "walk_in"
  | "phone"
  | "email"
  | "website"
  | "ota"
  | "corporate"
  | "agency"
  | "group";
type ReservationRoomStatus =
  | "reserved"
  | "assigned"
  | "checked_in"
  | "checked_out"
  | "cancelled"
  | "no_show";

type HotelProperty = {
  id: number;
  name: string;
  currency_id: number;
  currency_code?: string | null;
  currency_symbol?: string | null;
};

type HotelGuest = {
  id: number;
  first_name: string;
  last_name: string;
  email?: string | null;
  phone?: string | null;
};

type HotelRoomType = {
  id: number;
  name: string;
  property_id: number;
  default_rate?: number | null;
};

type HotelRoom = {
  id: number;
  room_number: string;
  room_type_id: number;
  room_type_name?: string | null;
};

type RatePlan = {
  id: number;
  name: string;
  property_id: number;
  currency_code?: string | null;
};

type RatePlanRoomType = {
  room_type_id: number;
  base_rate: number;
};

type ApiReservation = {
  id: number;
  property_id: number;
  property_name?: string | null;
  reservation_number: string;
  status: ReservationStatus;
  source: ReservationSource;
  channel_reference?: string | null;
  arrival_date: string;
  departure_date: string;
  nights: number;
  adults: number;
  children: number;
  total_amount: number | string;
  paid_amount: number | string;
  balance_due: number | string;
  payment_status: PaymentStatus;
  guest_first_name?: string | null;
  guest_last_name?: string | null;
  notes?: string | null;
};

type ApiReservationRoom = {
  id: number;
  room_type_id: number;
  room_id?: number | null;
  rate_plan_id?: number | null;
  room_type_name?: string | null;
  room_number?: string | null;
  check_in_date: string;
  check_out_date: string;
  adults: number;
  children: number;
  status: ReservationRoomStatus;
  assigned_at?: string | null;
  checked_in_at?: string | null;
  checked_out_at?: string | null;
};

type ReservationRecord = {
  id: number;
  propertyId: number;
  propertyName: string;
  reservationNumber: string;
  status: ReservationStatus;
  source: ReservationSource;
  channelReference?: string;
  arrivalDate: string;
  departureDate: string;
  nights: number;
  adults: number;
  children: number;
  guestName: string;
  totalAmount: number;
  paidAmount: number;
  balanceDue: number;
  paymentStatus: PaymentStatus;
  notes?: string;
};

type ReservationRoom = {
  id: number;
  roomTypeId: number;
  roomId?: number | null;
  ratePlanId?: number | null;
  roomTypeName: string;
  roomNumber: string | null;
  status: ReservationRoomStatus;
  checkInDate: string;
  checkOutDate: string;
  adults: number;
  children: number;
  assignedAt?: string | null;
  checkedInAt?: string | null;
  checkedOutAt?: string | null;
};

type ReservationFilters = {
  search: string;
  status: ReservationStatus | "all";
  paymentStatus: PaymentStatus | "all";
  source: ReservationSource | "all";
  dateRange: [Dayjs, Dayjs] | null;
  propertyId: number | "all";
};

type ReservationFormValues = {
  propertyId: number;
  guestId: number;
  dateRange: [Dayjs, Dayjs];
  adults: number;
  children: number;
  status: ReservationStatus;
  source: ReservationSource;
  paymentStatus: PaymentStatus;
  totalAmount: number;
  paidAmount: number;
  channelReference?: string;
  notes?: string;
};

type ReservationEditValues = {
  dateRange: [Dayjs, Dayjs];
  adults: number;
  children: number;
  status: ReservationStatus;
  source: ReservationSource;
  paymentStatus: PaymentStatus;
  totalAmount: number;
  paidAmount: number;
  channelReference?: string;
  notes?: string;
};

type ReservationAssignmentValues = {
  ratePlanId?: number;
  rooms: Array<{
    roomTypeId: number;
    roomId?: number;
    status: ReservationRoomStatus;
    adults: number;
    children: number;
  }>;
};

const statusLabels: Record<ReservationStatus, string> = {
  pending: "En attente",
  confirmed: "Confirmee",
  checked_in: "Arrivee",
  checked_out: "Depart",
  cancelled: "Annulee",
  no_show: "Absence",
};

const statusColors: Record<ReservationStatus, string> = {
  pending: "orange",
  confirmed: "blue",
  checked_in: "green",
  checked_out: "default",
  cancelled: "red",
  no_show: "volcano",
};

const paymentLabels: Record<PaymentStatus, string> = {
  unpaid: "Impayee",
  partial: "Partiel",
  paid: "Payee",
  refunded: "Remboursee",
};

const paymentColors: Record<PaymentStatus, string> = {
  unpaid: "red",
  partial: "gold",
  paid: "green",
  refunded: "cyan",
};

const roomStatusLabels: Record<ReservationRoomStatus, string> = {
  reserved: "Reservee",
  assigned: "Assignee",
  checked_in: "Arrivee",
  checked_out: "Depart",
  cancelled: "Annulee",
  no_show: "Absence",
};

const roomStatusColors: Record<ReservationRoomStatus, string> = {
  reserved: "orange",
  assigned: "blue",
  checked_in: "green",
  checked_out: "default",
  cancelled: "red",
  no_show: "volcano",
};

const sourceLabels: Record<ReservationSource, string> = {
  walk_in: "Sans reservation",
  phone: "Telephone",
  email: "Email",
  website: "Site web",
  ota: "OTA",
  corporate: "Entreprise",
  agency: "Agence",
  group: "Groupe",
};

const getNights = (arrival: string, departure: string) =>
  Math.max(1, dayjs(departure).diff(dayjs(arrival), "day"));

const ReservationsTab: React.FC = () => {
  const { selectedCompany } = useSelection();
  const { user, hasPermission } = useAuth();
  const [reservations, setReservations] = useState<ReservationRecord[]>([]);
  const [loading, setLoading] = useState(false);
  const [properties, setProperties] = useState<HotelProperty[]>([]);
  const [guests, setGuests] = useState<HotelGuest[]>([]);
  const [roomTypes, setRoomTypes] = useState<HotelRoomType[]>([]);
  const [rooms, setRooms] = useState<HotelRoom[]>([]);
  const [ratePlans, setRatePlans] = useState<RatePlan[]>([]);
  const [loadingRatePlans, setLoadingRatePlans] = useState(false);
  const [ratePlanRoomRates, setRatePlanRoomRates] = useState<Record<number, number>>(
    {}
  );
  const [loadingRatePlanRoomRates, setLoadingRatePlanRoomRates] = useState(false);
  const [selectedReservation, setSelectedReservation] =
    useState<ReservationRecord | null>(null);
  const [reservationRoomsMap, setReservationRoomsMap] = useState<
    Record<number, ReservationRoom[]>
  >({});
  const [reservationRoomsUpdatedMap, setReservationRoomsUpdatedMap] = useState<
    Record<number, Dayjs>
  >({});
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [assignmentsLoading, setAssignmentsLoading] = useState(false);
  const [tablePage, setTablePage] = useState(1);
  const [tablePageSize, setTablePageSize] = useState(6);
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [editingReservation, setEditingReservation] =
    useState<ReservationRecord | null>(null);
  const [isAssignOpen, setIsAssignOpen] = useState(false);
  const [assignmentLoading, setAssignmentLoading] = useState(false);
  const [form] = Form.useForm<ReservationFormValues>();
  const [editForm] = Form.useForm<ReservationEditValues>();
  const [assignmentForm] = Form.useForm<ReservationAssignmentValues>();
  const selectedRatePlanId = Form.useWatch("ratePlanId", assignmentForm);
  const assignmentRooms =
    (Form.useWatch(
      "rooms",
      assignmentForm
    ) as ReservationAssignmentValues["rooms"] | undefined) || [];
  const [filters, setFilters] = useState<ReservationFilters>({
    search: "",
    status: "all",
    paymentStatus: "all",
    source: "all",
    dateRange: null,
    propertyId: "all",
  });

  const propertyMap = useMemo(() => {
    const map = new Map<number, HotelProperty>();
    properties.forEach((property) => map.set(property.id, property));
    return map;
  }, [properties]);

  const roomTypeMap = useMemo(() => {
    const map = new Map<number, HotelRoomType>();
    roomTypes.forEach((roomType) => map.set(roomType.id, roomType));
    return map;
  }, [roomTypes]);

  const requiresCompanySelection = Boolean(user?.is_superadmin && !selectedCompany);
  const canCreateReservations = Boolean(
    hasPermission?.("Hotellerie.Reservations.create")
  );
  const canEditReservations = Boolean(
    hasPermission?.("Hotellerie.Reservations.edit")
  );

  const formatMoney = useCallback(
    (value: number, propertyId?: number) => {
      const formatted = new Intl.NumberFormat("fr-FR", {
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      }).format(Number(value || 0));
      const property = propertyId ? propertyMap.get(propertyId) : undefined;
      const suffix = property?.currency_symbol || property?.currency_code;
      return suffix ? `${formatted} ${suffix}` : formatted;
    },
    [propertyMap]
  );

  const formatMoneyWithSuffix = useCallback((value: number, suffix?: string | null) => {
    const formatted = new Intl.NumberFormat("fr-FR", {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(Number(value || 0));
    return suffix ? `${formatted} ${suffix}` : formatted;
  }, []);

  const fetchProperties = useCallback(async () => {
    if (requiresCompanySelection) {
      return;
    }
    try {
      const params = selectedCompany ? { company_id: selectedCompany } : {};
      const response = await axios.get(`${API_CONFIG.API_URL}/hotel/properties`, {
        params,
      });
      if (response.data.success) {
        setProperties(response.data.data || []);
      }
    } catch (error: any) {
      console.error("[HOTEL] Error fetching properties:", error);
      message.error(
        error.response?.data?.message || "Erreur lors du chargement des proprietes"
      );
    }
  }, [requiresCompanySelection, selectedCompany]);

  const fetchGuests = useCallback(async () => {
    if (requiresCompanySelection) {
      return;
    }
    try {
      const params = selectedCompany ? { company_id: selectedCompany } : {};
      const response = await axios.get(`${API_CONFIG.API_URL}/hotel/guests`, {
        params,
      });
      if (response.data.success) {
        setGuests(response.data.data || []);
      }
    } catch (error: any) {
      console.error("[HOTEL] Error fetching guests:", error);
      message.error(
        error.response?.data?.message || "Erreur lors du chargement des clients"
      );
    }
  }, [requiresCompanySelection, selectedCompany]);

  const fetchRoomTypes = useCallback(
    async (propertyId?: number) => {
      if (requiresCompanySelection) {
        return;
      }
      try {
        const params: Record<string, string | number> = {};
        if (selectedCompany) {
          params.company_id = selectedCompany;
        }
        if (propertyId) {
          params.property_id = propertyId;
        }
        const response = await axios.get(`${API_CONFIG.API_URL}/hotel/room-types`, {
          params,
        });
        if (response.data.success) {
          setRoomTypes(response.data.data || []);
        }
      } catch (error: any) {
        console.error("[HOTEL] Error fetching room types:", error);
        message.error(
          error.response?.data?.message ||
            "Erreur lors du chargement des types de chambres"
        );
      }
    },
    [requiresCompanySelection, selectedCompany]
  );

  const fetchRooms = useCallback(
    async (propertyId?: number, roomTypeId?: number) => {
      if (requiresCompanySelection) {
        return;
      }
      try {
        const params: Record<string, string | number> = {};
        if (selectedCompany) {
          params.company_id = selectedCompany;
        }
        if (propertyId) {
          params.property_id = propertyId;
        }
        if (roomTypeId) {
          params.room_type_id = roomTypeId;
        }
        const response = await axios.get(`${API_CONFIG.API_URL}/hotel/rooms`, {
          params,
        });
        if (response.data.success) {
          setRooms(response.data.data || []);
        }
      } catch (error: any) {
        console.error("[HOTEL] Error fetching rooms:", error);
        message.error(
          error.response?.data?.message || "Erreur lors du chargement des chambres"
        );
      }
    },
    [requiresCompanySelection, selectedCompany]
  );

  const fetchRatePlans = useCallback(
    async (propertyId?: number) => {
      if (requiresCompanySelection) {
        return;
      }
      setLoadingRatePlans(true);
      try {
        const params: Record<string, string | number> = { is_active: "true" };
        if (selectedCompany) {
          params.company_id = selectedCompany;
        }
        if (propertyId) {
          params.property_id = propertyId;
        }
        const response = await axios.get(`${API_CONFIG.API_URL}/hotel/rate-plans`, {
          params,
        });
        if (response.data.success) {
          setRatePlans(response.data.data || []);
        }
      } catch (error: any) {
        console.error("[HOTEL] Error fetching rate plans:", error);
        message.error(
          error.response?.data?.message ||
            "Erreur lors du chargement des plans tarifaires"
        );
      } finally {
        setLoadingRatePlans(false);
      }
    },
    [requiresCompanySelection, selectedCompany]
  );

  const fetchRatePlanRoomTypes = useCallback(
    async (ratePlanId: number) => {
      if (requiresCompanySelection) {
        return;
      }
      setLoadingRatePlanRoomRates(true);
      try {
        const params = selectedCompany ? { company_id: selectedCompany } : {};
        const response = await axios.get(
          `${API_CONFIG.API_URL}/hotel/rate-plans/${ratePlanId}/room-types`,
          { params }
        );
        if (response.data.success) {
          const map: Record<number, number> = {};
          (response.data.data || []).forEach((row: RatePlanRoomType) => {
            map[Number(row.room_type_id)] = Number(row.base_rate || 0);
          });
          setRatePlanRoomRates(map);
        }
      } catch (error: any) {
        console.error("[HOTEL] Error fetching rate plan room types:", error);
        message.error(
          error.response?.data?.message ||
            "Erreur lors du chargement des tarifs du plan"
        );
      } finally {
        setLoadingRatePlanRoomRates(false);
      }
    },
    [requiresCompanySelection, selectedCompany]
  );

  const fetchReservations = useCallback(async () => {
    if (requiresCompanySelection) {
      return;
    }
    setLoading(true);
    try {
      const params: Record<string, string | number> = {};
      if (selectedCompany) {
        params.company_id = selectedCompany;
      }
      if (filters.propertyId !== "all") {
        params.property_id = filters.propertyId;
      }
      if (filters.status !== "all") {
        params.status = filters.status;
      }
      if (filters.dateRange) {
        params.start_date = filters.dateRange[0].format("YYYY-MM-DD");
        params.end_date = filters.dateRange[1].format("YYYY-MM-DD");
      }

      const response = await axios.get(`${API_CONFIG.API_URL}/hotel/reservations`, {
        params,
      });
      if (response.data.success) {
        const mapped = (response.data.data || []).map((row: ApiReservation) => ({
          id: row.id,
          propertyId: Number(row.property_id),
          propertyName: row.property_name || `Propriete #${row.property_id}`,
          reservationNumber: row.reservation_number,
          status: row.status,
          source: row.source,
          channelReference: row.channel_reference || undefined,
          arrivalDate: row.arrival_date,
          departureDate: row.departure_date,
          nights: Number(row.nights || getNights(row.arrival_date, row.departure_date)),
          adults: Number(row.adults || 1),
          children: Number(row.children || 0),
          guestName:
            `${row.guest_first_name || ""} ${row.guest_last_name || ""}`.trim() ||
            "Client",
          totalAmount: Number(row.total_amount || 0),
          paidAmount: Number(row.paid_amount || 0),
          balanceDue: Number(row.balance_due || 0),
          paymentStatus: row.payment_status,
          notes: row.notes || undefined,
        }));
        setReservations(mapped);
      }
    } catch (error: any) {
      console.error("[HOTEL] Error fetching reservations:", error);
      message.error(
        error.response?.data?.message ||
          "Erreur lors du chargement des reservations"
      );
    } finally {
      setLoading(false);
    }
  }, [filters.dateRange, filters.propertyId, filters.status, requiresCompanySelection, selectedCompany]);

  const updateReservationRooms = useCallback(
    (reservationId: number, rooms: ReservationRoom[]) => {
      setReservationRoomsMap((prev) => ({ ...prev, [reservationId]: rooms }));
      setReservationRoomsUpdatedMap((prev) => ({
        ...prev,
        [reservationId]: dayjs(),
      }));
    },
    []
  );

  const fetchReservationRoomsFor = useCallback(
    async (reservationId: number, suppressError = false) => {
      if (requiresCompanySelection) {
        return null;
      }
      try {
        const params = selectedCompany ? { company_id: selectedCompany } : {};
        const response = await axios.get(
          `${API_CONFIG.API_URL}/hotel/reservations/${reservationId}`,
          { params }
        );
        if (response.data.success) {
          const rooms = (response.data.data?.rooms || []).map(
            (room: ApiReservationRoom) => ({
              id: room.id,
              roomTypeId: Number(room.room_type_id),
              roomId: room.room_id ? Number(room.room_id) : null,
              ratePlanId: room.rate_plan_id ? Number(room.rate_plan_id) : null,
              roomTypeName:
                room.room_type_name || `Type #${room.room_type_id}`,
              roomNumber: room.room_number || null,
              status: room.status,
              checkInDate: room.check_in_date,
              checkOutDate: room.check_out_date,
              adults: Number(room.adults || 0),
              children: Number(room.children || 0),
              assignedAt: room.assigned_at || null,
              checkedInAt: room.checked_in_at || null,
              checkedOutAt: room.checked_out_at || null,
            })
          );
          updateReservationRooms(reservationId, rooms);
          return rooms;
        }
      } catch (error: any) {
        console.error("[HOTEL] Error fetching reservation details:", error);
        if (!suppressError) {
          message.error(
            error.response?.data?.message ||
              "Erreur lors du chargement de la reservation"
          );
        }
      }
      return null;
    },
    [requiresCompanySelection, selectedCompany, updateReservationRooms]
  );

  useEffect(() => {
    fetchProperties();
    fetchGuests();
  }, [fetchGuests, fetchProperties]);

  useEffect(() => {
    fetchReservations();
  }, [fetchReservations]);

  useEffect(() => {
    if (!isAssignOpen || !selectedReservation) {
      return;
    }
    fetchRoomTypes(selectedReservation.propertyId);
    fetchRooms(selectedReservation.propertyId);
    fetchRatePlans(selectedReservation.propertyId);
  }, [
    fetchRatePlans,
    fetchRoomTypes,
    fetchRooms,
    isAssignOpen,
    selectedReservation,
  ]);

  useEffect(() => {
    if (!isAssignOpen || ratePlans.length === 0) {
      return;
    }
    const currentRatePlanId = assignmentForm.getFieldValue("ratePlanId");
    if (!currentRatePlanId) {
      assignmentForm.setFieldValue("ratePlanId", ratePlans[0].id);
    }
  }, [assignmentForm, isAssignOpen, ratePlans]);

  useEffect(() => {
    if (!isAssignOpen) {
      return;
    }
    if (!selectedRatePlanId) {
      setRatePlanRoomRates({});
      return;
    }
    fetchRatePlanRoomTypes(selectedRatePlanId);
  }, [fetchRatePlanRoomTypes, isAssignOpen, selectedRatePlanId]);

  useEffect(() => {
    setTablePage(1);
  }, [
    filters.dateRange,
    filters.paymentStatus,
    filters.propertyId,
    filters.search,
    filters.source,
    filters.status,
  ]);

  useEffect(() => {
    if (!selectedReservation) {
      setDetailsLoading(false);
      return;
    }

    let isMounted = true;
    const loadDetails = async () => {
      setDetailsLoading(true);
      await fetchReservationRoomsFor(selectedReservation.id);
      if (isMounted) {
        setDetailsLoading(false);
      }
    };

    loadDetails();
    return () => {
      isMounted = false;
    };
  }, [fetchReservationRoomsFor, selectedReservation]);

  const filteredReservations = useMemo(() => {
    const search = filters.search.trim().toLowerCase();
    return reservations.filter((reservation) => {
      const matchesSearch =
        !search ||
        reservation.reservationNumber.toLowerCase().includes(search) ||
        reservation.guestName.toLowerCase().includes(search) ||
        reservation.channelReference?.toLowerCase().includes(search);

      const matchesPayment =
        filters.paymentStatus === "all" ||
        reservation.paymentStatus === filters.paymentStatus;
      const matchesSource =
        filters.source === "all" || reservation.source === filters.source;

      return matchesSearch && matchesPayment && matchesSource;
    });
  }, [filters.paymentStatus, filters.search, filters.source, reservations]);

  const stats = useMemo(() => {
    const today = dayjs().startOf("day");
    const arrivalsToday = reservations.filter((reservation) =>
      dayjs(reservation.arrivalDate).isSame(today, "day")
    ).length;
    const departuresToday = reservations.filter((reservation) =>
      dayjs(reservation.departureDate).isSame(today, "day")
    ).length;
    const inHouse = reservations.filter((reservation) => {
      const arrival = dayjs(reservation.arrivalDate);
      const departure = dayjs(reservation.departureDate);
      const isInRange =
        (today.isSame(arrival, "day") || today.isAfter(arrival, "day")) &&
        today.isBefore(departure, "day");
      return (
        isInRange &&
        (reservation.status === "checked_in" || reservation.status === "confirmed")
      );
    }).length;
    const balanceDue = reservations.reduce(
      (sum, reservation) => sum + reservation.balanceDue,
      0
    );

    return {
      arrivalsToday,
      departuresToday,
      inHouse,
      balanceDue,
    };
  }, [reservations]);

  const handleDateRangeChange: RangePickerProps["onChange"] = (values) => {
    const start = values?.[0] || null;
    const end = values?.[1] || null;
    if (!start || !end) {
      setFilters((prev) => ({ ...prev, dateRange: null }));
      return;
    }
    setFilters((prev) => ({ ...prev, dateRange: [start, end] }));
  };

  const handleResetFilters = () => {
    setFilters((prev) => ({
      ...prev,
      search: "",
      status: "all",
      paymentStatus: "all",
      source: "all",
      dateRange: null,
      propertyId: "all",
    }));
  };

  const handleCreateReservation = async () => {
    if (!canCreateReservations) {
      message.warning("Vous n'avez pas les droits pour creer une reservation.");
      return;
    }
    const values = await form.validateFields();
    const property = propertyMap.get(values.propertyId);

    if (!property) {
      message.warning("Selectionnez une propriete valide.");
      return;
    }

    const [arrival, departure] = values.dateRange;
    const totalAmount = Number(values.totalAmount || 0);
    const paidAmount = Number(values.paidAmount || 0);
    const balanceDue = Math.max(0, totalAmount - paidAmount);

    try {
      const payload = {
        ...(selectedCompany ? { company_id: selectedCompany } : {}),
        property_id: values.propertyId,
        guest_id: values.guestId,
        currency_id: Number(property.currency_id),
        arrival_date: arrival.format("YYYY-MM-DD"),
        departure_date: departure.format("YYYY-MM-DD"),
        status: values.status,
        source: values.source,
        channel_reference: values.channelReference || null,
        adults: values.adults,
        children: values.children,
        total_amount: totalAmount,
        paid_amount: paidAmount,
        balance_due: balanceDue,
        payment_status: values.paymentStatus,
        notes: values.notes || null,
      };

      const response = await axios.post(
        `${API_CONFIG.API_URL}/hotel/reservations`,
        payload
      );

      if (response.data.success) {
        message.success("Reservation creee avec succes");
        setIsCreateOpen(false);
        form.resetFields();
        fetchReservations();
      }
    } catch (error: any) {
      console.error("[HOTEL] Error creating reservation:", error);
      message.error(
        error.response?.data?.message ||
          "Erreur lors de la creation de la reservation"
      );
    }
  };

  const openEditReservation = (reservation: ReservationRecord) => {
    setEditingReservation(reservation);
    editForm.setFieldsValue({
      dateRange: [dayjs(reservation.arrivalDate), dayjs(reservation.departureDate)],
      adults: reservation.adults,
      children: reservation.children,
      status: reservation.status,
      source: reservation.source,
      paymentStatus: reservation.paymentStatus,
      totalAmount: reservation.totalAmount,
      paidAmount: reservation.paidAmount,
      channelReference: reservation.channelReference,
      notes: reservation.notes,
    });
    setIsEditOpen(true);
  };

  const handleUpdateReservation = async () => {
    if (!editingReservation) {
      return;
    }
    if (!canEditReservations) {
      message.warning("Vous n'avez pas les droits pour modifier cette reservation.");
      return;
    }
    const values = await editForm.validateFields();
    const [arrival, departure] = values.dateRange;
    const totalAmount = Number(values.totalAmount || 0);
    const paidAmount = Number(values.paidAmount || 0);
    const balanceDue = Math.max(0, totalAmount - paidAmount);

    try {
      const payload = {
        ...(selectedCompany ? { company_id: selectedCompany } : {}),
        arrival_date: arrival.format("YYYY-MM-DD"),
        departure_date: departure.format("YYYY-MM-DD"),
        status: values.status,
        source: values.source,
        channel_reference: values.channelReference || null,
        adults: values.adults,
        children: values.children,
        total_amount: totalAmount,
        paid_amount: paidAmount,
        balance_due: balanceDue,
        payment_status: values.paymentStatus,
        notes: values.notes || null,
      };

      const response = await axios.put(
        `${API_CONFIG.API_URL}/hotel/reservations/${editingReservation.id}`,
        payload
      );

      if (response.data.success) {
        message.success("Reservation mise a jour");
        setIsEditOpen(false);
        setEditingReservation(null);
        fetchReservations();
      }
    } catch (error: any) {
      console.error("[HOTEL] Error updating reservation:", error);
      message.error(
        error.response?.data?.message ||
          "Erreur lors de la mise a jour de la reservation"
      );
    }
  };

  const syncReservationRoomsStatus = useCallback(
    async (reservation: ReservationRecord, nextStatus: ReservationStatus) => {
      if (nextStatus !== "checked_in" && nextStatus !== "checked_out") {
        return true;
      }

      const rooms = await fetchReservationRoomsFor(reservation.id, true);
      if (!rooms) {
        message.error("Impossible de charger les chambres associees.");
        return false;
      }

      if (rooms.length === 0) {
        message.warning("Aucune chambre n'est associee a cette reservation.");
        if (nextStatus === "checked_in") {
          openAssignmentModal();
        }
        return false;
      }

      const assignedRooms = rooms.filter(
        (room: ReservationRoom) => room.roomId
      );
      if (nextStatus === "checked_in" && assignedRooms.length === 0) {
        message.warning("Attribuez au moins une chambre avant l'arrivee.");
        openAssignmentModal();
        return false;
      }

      try {
        const payload = {
          ...(selectedCompany ? { company_id: selectedCompany } : {}),
          rooms: rooms.map((room: ReservationRoom) => ({
            room_type_id: room.roomTypeId,
            room_id: room.roomId || null,
            status: nextStatus,
            adults: room.adults,
            children: room.children,
            check_in_date: reservation.arrivalDate,
            check_out_date: reservation.departureDate,
          })),
        };

        const response = await axios.put(
          `${API_CONFIG.API_URL}/hotel/reservations/${reservation.id}/rooms`,
          payload
        );

        if (response.data.success) {
          updateReservationRooms(
            reservation.id,
            rooms.map((room: ReservationRoom) => ({ ...room, status: nextStatus }))
          );
          return true;
        }
      } catch (error: any) {
        console.error("[HOTEL] Error syncing reservation rooms:", error);
        message.error(
          error.response?.data?.message ||
            "Erreur lors de la mise a jour des chambres"
        );
      }

      return false;
    },
    [fetchReservationRoomsFor, openAssignmentModal, selectedCompany, updateReservationRooms]
  );

  const handleQuickStatusUpdate = async (
    reservation: ReservationRecord,
    nextStatus: ReservationStatus
  ) => {
    if (!canEditReservations) {
      message.warning("Vous n'avez pas les droits pour changer le statut.");
      return;
    }
    if (["cancelled", "no_show"].includes(reservation.status)) {
      message.warning("Le statut actuel ne permet pas cette action.");
      return;
    }
    const roomsSynced = await syncReservationRoomsStatus(reservation, nextStatus);
    if (!roomsSynced) {
      return;
    }
    try {
      const payload = {
        ...(selectedCompany ? { company_id: selectedCompany } : {}),
        status: nextStatus,
      };
      const response = await axios.put(
        `${API_CONFIG.API_URL}/hotel/reservations/${reservation.id}`,
        payload
      );
      if (response.data.success) {
        message.success("Statut mis a jour.");
        fetchReservations();
        if (selectedReservation?.id === reservation.id) {
          setSelectedReservation((prev) =>
            prev ? { ...prev, status: nextStatus } : prev
          );
        }
      }
    } catch (error: any) {
      console.error("[HOTEL] Error updating reservation status:", error);
      message.error(
        error.response?.data?.message ||
          "Erreur lors de la mise a jour du statut"
      );
    }
  };

  function openAssignmentModal() {
    if (!selectedReservation) {
      return;
    }
    const existingRooms = reservationRoomsMap[selectedReservation.id] || [];
    const defaultRatePlanId =
      existingRooms.find((room) => room.ratePlanId)?.ratePlanId || undefined;
    const defaultRooms =
      existingRooms.length > 0
        ? existingRooms
        : [
            {
              id: 0,
              roomTypeId: roomTypes[0]?.id || 0,
              roomId: null,
              roomTypeName: roomTypes[0]?.name || "",
              roomNumber: null,
              status: "reserved" as ReservationRoomStatus,
              checkInDate: selectedReservation.arrivalDate,
              checkOutDate: selectedReservation.departureDate,
              adults: selectedReservation.adults,
              children: selectedReservation.children,
            },
          ];

    assignmentForm.setFieldsValue({
      rooms: defaultRooms.map((room) => ({
        roomTypeId: room.roomTypeId,
        roomId: room.roomId || undefined,
        status: room.status,
        adults: room.adults || selectedReservation.adults,
        children: room.children || selectedReservation.children,
      })),
      ratePlanId: defaultRatePlanId,
    });
    setIsAssignOpen(true);
  }

  const handleSaveAssignments = async () => {
    if (!selectedReservation) {
      return;
    }
    if (!canEditReservations) {
      message.warning("Vous n'avez pas les droits pour modifier les chambres.");
      return;
    }
    const values =
      (await assignmentForm.validateFields()) as ReservationAssignmentValues;
    const payloadRooms = (values.rooms || [])
      .filter(
        (room: ReservationAssignmentValues["rooms"][number]) =>
          room.roomTypeId
      )
      .map((room: ReservationAssignmentValues["rooms"][number]) => ({
        room_type_id: room.roomTypeId,
        room_id: room.roomId || null,
        rate_plan_id: values.ratePlanId || null,
        status: room.status,
        adults: room.adults,
        children: room.children,
        check_in_date: selectedReservation.arrivalDate,
        check_out_date: selectedReservation.departureDate,
      }));

    setAssignmentLoading(true);
    try {
      const payload = {
        ...(selectedCompany ? { company_id: selectedCompany } : {}),
        rooms: payloadRooms,
      };
      const response = await axios.put(
        `${API_CONFIG.API_URL}/hotel/reservations/${selectedReservation.id}/rooms`,
        payload
      );
      if (response.data.success) {
        message.success("Chambres mises a jour.");
        setIsAssignOpen(false);
        await fetchReservationRoomsFor(selectedReservation.id, true);
        fetchReservations();
      }
    } catch (error: any) {
      console.error("[HOTEL] Error updating reservation rooms:", error);
      message.error(
        error.response?.data?.message ||
          "Erreur lors de la mise a jour des chambres"
      );
    } finally {
      setAssignmentLoading(false);
    }
  };

  const handleCloseDrawer = () => {
    setSelectedReservation(null);
    setDetailsLoading(false);
    setIsAssignOpen(false);
  };

  const handleRefreshAssignments = async () => {
    const startIndex = (tablePage - 1) * tablePageSize;
    const visibleReservations = filteredReservations.slice(
      startIndex,
      startIndex + tablePageSize
    );
    if (visibleReservations.length === 0) {
      return;
    }

    setAssignmentsLoading(true);
    const results = await Promise.all(
      visibleReservations.map((reservation) =>
        fetchReservationRoomsFor(reservation.id, true)
      )
    );
    setAssignmentsLoading(false);

    const failed = results.filter((result) => result === null).length;
    if (failed === 0) {
      message.success("Attributions mises a jour.");
    } else {
      message.warning("Certaines attributions n'ont pas pu etre chargees.");
    }
  };

  const handleRefreshSelectedReservation = async () => {
    if (!selectedReservation) {
      return;
    }
    setDetailsLoading(true);
    await fetchReservationRoomsFor(selectedReservation.id);
    setDetailsLoading(false);
  };

  const buildRoomsSummary = (reservationId: number) => {
    const rooms = reservationRoomsMap[reservationId];
    if (!rooms) {
    return <Text type="secondary">Non chargee</Text>;
    }
    if (rooms.length === 0) {
    return <Text type="secondary">Aucune chambre</Text>;
    }

    const assignedNumbers = rooms
      .map((room) => room.roomNumber)
      .filter((roomNumber): roomNumber is string => Boolean(roomNumber));
    const assignedCount = assignedNumbers.length;
    const totalCount = rooms.length;

      if (assignedCount === 0) {
        return (
          <Space direction="vertical" size={0}>
            <Text>Non attribuee</Text>
            <Text type="secondary">{`0/${totalCount} attribuees`}</Text>
          </Space>
        );
      }

    const displayNumbers =
      assignedNumbers.length > 3
        ? `${assignedNumbers.slice(0, 2).join(", ")} +${
            assignedNumbers.length - 2
          }`
        : assignedNumbers.join(", ");

      return (
        <Space direction="vertical" size={0}>
          <Text>{displayNumbers}</Text>
          <Text type="secondary">{`${assignedCount}/${totalCount} attribuees`}</Text>
        </Space>
      );
    };

  const handleTableChange = (pagination: { current?: number; pageSize?: number }) => {
    setTablePage(pagination.current || 1);
    setTablePageSize(pagination.pageSize || 6);
  };

  const estimatedTotal = useMemo(() => {
    if (!selectedReservation) {
      return 0;
    }
    const nights =
      selectedReservation.nights ||
      getNights(selectedReservation.arrivalDate, selectedReservation.departureDate);
    return assignmentRooms.reduce((sum, room) => {
      const roomType = roomTypeMap.get(room.roomTypeId);
      const planRate =
        selectedRatePlanId && room.roomTypeId
          ? ratePlanRoomRates[room.roomTypeId]
          : undefined;
      const baseRate =
        planRate !== undefined && planRate !== null
          ? planRate
          : roomType?.default_rate ?? 0;
      return sum + Number(baseRate || 0) * Number(nights || 1);
    }, 0);
  }, [
    assignmentRooms,
    ratePlanRoomRates,
    roomTypeMap,
    selectedRatePlanId,
    selectedReservation,
  ]);

  const estimateCurrency = useMemo(() => {
    if (!selectedReservation) {
      return null;
    }
    const plan = ratePlans.find((item) => item.id === selectedRatePlanId);
    const property = propertyMap.get(selectedReservation.propertyId);
    return plan?.currency_code || property?.currency_symbol || property?.currency_code || null;
  }, [propertyMap, ratePlans, selectedRatePlanId, selectedReservation]);

  const estimateNote = useMemo(() => {
    if (!selectedReservation) {
      return "";
    }
    if (loadingRatePlanRoomRates) {
      return "Chargement des tarifs du plan en cours...";
    }
    if (!selectedRatePlanId) {
      return "Aucun plan choisi: tarif par defaut du type de chambre.";
    }
    if (Object.keys(ratePlanRoomRates).length === 0) {
      return "Aucun tarif trouve pour ce plan. Verifiez les montants.";
    }
    return "Calcule sur le plan tarifaire, le type de chambre et les nuits.";
  }, [loadingRatePlanRoomRates, ratePlanRoomRates, selectedRatePlanId, selectedReservation]);

  const columns: ColumnsType<ReservationRecord> = [
      {
        title: "Reservation",
      dataIndex: "reservationNumber",
      key: "reservationNumber",
      render: (_value, record) => (
        <Space direction="vertical" size={0}>
          <Text strong>{record.reservationNumber}</Text>
          {record.channelReference ? (
            <Text type="secondary">{record.channelReference}</Text>
          ) : (
            <Text type="secondary">{sourceLabels[record.source]}</Text>
          )}
        </Space>
      ),
    },
      {
        title: "Client",
      dataIndex: "guestName",
      key: "guestName",
      render: (value, record) => (
        <Space direction="vertical" size={0}>
          <Text>{value}</Text>
          <Tag color="blue">{sourceLabels[record.source]}</Tag>
        </Space>
      ),
    },
      {
        title: "Sejour",
      key: "stay",
      render: (_value, record) => (
        <Space direction="vertical" size={0}>
          <Text>
            {dayjs(record.arrivalDate).format("DD/MM/YYYY")} -{" "}
            {dayjs(record.departureDate).format("DD/MM/YYYY")}
          </Text>
            <Text type="secondary">{record.nights} nuit(s)</Text>
        </Space>
      ),
    },
      {
        title: "Propriete",
      dataIndex: "propertyName",
      key: "propertyName",
    },
      {
        title: "Chambres attribuees",
      key: "roomsAssigned",
      render: (_value, record) => buildRoomsSummary(record.id),
    },
      {
        title: "Statut",
      dataIndex: "status",
      key: "status",
      render: (value: ReservationStatus) => (
        <Tag color={statusColors[value]}>{statusLabels[value]}</Tag>
      ),
    },
      {
        title: "Paiement",
      key: "payment",
      render: (_value, record) => (
        <Space direction="vertical" size={0}>
          <Tag color={paymentColors[record.paymentStatus]}>
            {paymentLabels[record.paymentStatus]}
          </Tag>
            <Text type="secondary">
              Solde: {formatMoney(record.balanceDue, record.propertyId)}
            </Text>
        </Space>
      ),
    },
    {
      title: "Total",
      dataIndex: "totalAmount",
      key: "totalAmount",
      render: (value: number, record) => formatMoney(value, record.propertyId),
    },
      {
        title: "Actions",
      key: "actions",
      render: (_value, record) => (
        <Space size={4}>
          <Button type="link" onClick={() => setSelectedReservation(record)}>
            View
          </Button>
          {canEditReservations ? (
            <Button type="link" onClick={() => openEditReservation(record)}>
              Modifier
            </Button>
          ) : null}
          {canEditReservations &&
          (record.status === "confirmed" || record.status === "pending") ? (
            <Button
              type="link"
              onClick={() => handleQuickStatusUpdate(record, "checked_in")}
            >
              Arrivee
            </Button>
          ) : null}
          {canEditReservations && record.status === "checked_in" ? (
            <Button
              type="link"
              onClick={() => handleQuickStatusUpdate(record, "checked_out")}
            >
              Depart
            </Button>
          ) : null}
        </Space>
      ),
    },
  ];

    const roomColumns: ColumnsType<ReservationRoom> = [
      {
        title: "Chambre",
        dataIndex: "roomNumber",
        key: "roomNumber",
        render: (value: string | null, record) =>
          value ? value : `Non assignee (${record.roomTypeName})`,
      },
      {
        title: "Type de chambre",
        dataIndex: "roomTypeName",
        key: "roomTypeName",
      },
      {
        title: "Sejour",
        key: "stay",
        render: (_value, record) =>
          `${dayjs(record.checkInDate).format("DD/MM")} - ${dayjs(
            record.checkOutDate
          ).format("DD/MM")}`,
      },
      {
        title: "Clients",
        key: "guests",
        render: (_value, record) =>
          `${record.adults} adulte(s), ${record.children} enfant(s)`,
      },
      {
        title: "Statut",
        dataIndex: "status",
        key: "status",
        render: (value: ReservationRoomStatus) => (
          <Tag color={roomStatusColors[value]}>{roomStatusLabels[value]}</Tag>
        ),
    },
  ];

  if (requiresCompanySelection) {
    return (
      <Card>
        <Alert
          message="Selection requise"
          description="Selectionnez une entreprise pour acceder aux reservations."
          type="info"
          showIcon
        />
      </Card>
    );
  }

  return (
    <Space direction="vertical" size={16} style={{ width: "100%" }}>
      <Row gutter={[16, 16]}>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic title="Arrivees du jour" value={stats.arrivalsToday} />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic title="Departs du jour" value={stats.departuresToday} />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic title="En chambre" value={stats.inHouse} />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="Solde du"
              value={stats.balanceDue}
              formatter={(value) => formatMoney(Number(value))}
            />
          </Card>
        </Col>
      </Row>

      <Card
        title="Filtres"
        extra={
          <Button icon={<FilterOutlined />} onClick={handleResetFilters}>
            Reinitialiser
          </Button>
        }
      >
        <Form layout="vertical">
          <Row gutter={[16, 16]}>
            <Col xs={24} md={6}>
              <Form.Item label="Propriete">
                <Select
                  value={filters.propertyId}
                  onChange={(value) =>
                    setFilters((prev) => ({ ...prev, propertyId: value }))
                  }
                  options={[
                    { value: "all", label: "Tous" },
                    ...properties.map((property) => ({
                      value: property.id,
                      label: property.name,
                    })),
                  ]}
                />
              </Form.Item>
            </Col>
            <Col xs={24} md={6}>
              <Form.Item label="Recherche">
                <Input
                  placeholder="Reservation, client, ref"
                  value={filters.search}
                  onChange={(event) =>
                    setFilters((prev) => ({
                      ...prev,
                      search: event.target.value,
                    }))
                  }
                  prefix={<SearchOutlined />}
                  allowClear
                />
              </Form.Item>
            </Col>
            <Col xs={24} md={4}>
              <Form.Item label="Statut">
                <Select<ReservationFilters["status"]>
                  value={filters.status}
                  onChange={(value) =>
                    setFilters((prev) => ({ ...prev, status: value }))
                  }
                  options={[
                    { value: "all", label: "Tous" },
                    { value: "pending", label: statusLabels.pending },
                    { value: "confirmed", label: statusLabels.confirmed },
                    { value: "checked_in", label: statusLabels.checked_in },
                    { value: "checked_out", label: statusLabels.checked_out },
                    { value: "cancelled", label: statusLabels.cancelled },
                    { value: "no_show", label: statusLabels.no_show },
                  ]}
                />
              </Form.Item>
            </Col>
            <Col xs={24} md={4}>
              <Form.Item label="Paiement">
                <Select<ReservationFilters["paymentStatus"]>
                  value={filters.paymentStatus}
                  onChange={(value) =>
                    setFilters((prev) => ({ ...prev, paymentStatus: value }))
                  }
                  options={[
                    { value: "all", label: "Tous" },
                    { value: "unpaid", label: paymentLabels.unpaid },
                    { value: "partial", label: paymentLabels.partial },
                    { value: "paid", label: paymentLabels.paid },
                    { value: "refunded", label: paymentLabels.refunded },
                  ]}
                />
              </Form.Item>
            </Col>
            <Col xs={24} md={4}>
              <Form.Item label="Source">
                <Select<ReservationFilters["source"]>
                  value={filters.source}
                  onChange={(value) =>
                    setFilters((prev) => ({ ...prev, source: value }))
                  }
                  options={[
                    { value: "all", label: "Tous" },
                    { value: "walk_in", label: sourceLabels.walk_in },
                    { value: "phone", label: sourceLabels.phone },
                    { value: "email", label: sourceLabels.email },
                    { value: "website", label: sourceLabels.website },
                    { value: "ota", label: sourceLabels.ota },
                    { value: "corporate", label: sourceLabels.corporate },
                    { value: "agency", label: sourceLabels.agency },
                    { value: "group", label: sourceLabels.group },
                  ]}
                />
              </Form.Item>
            </Col>
            <Col xs={24} md={6}>
              <Form.Item label="Periode">
                <RangePicker
                  value={filters.dateRange ?? null}
                  onChange={handleDateRangeChange}
                  allowEmpty={[true, true]}
                />
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Card>

      <Card
        title="Reservations"
        extra={
          <Space>
            <Button
              icon={<ReloadOutlined />}
              onClick={handleRefreshAssignments}
              loading={assignmentsLoading}
              disabled={filteredReservations.length === 0}
            >
              Rafraichir attributions
            </Button>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => setIsCreateOpen(true)}
              disabled={
                !canCreateReservations ||
                properties.length === 0 ||
                guests.length === 0
              }
            >
              Nouvelle reservation
            </Button>
          </Space>
        }
      >
        <Table
          columns={columns}
          dataSource={filteredReservations}
          rowKey="id"
          loading={loading}
          pagination={{ current: tablePage, pageSize: tablePageSize }}
          onChange={handleTableChange}
        />
      </Card>

      <Drawer
        title="Details reservation"
        open={Boolean(selectedReservation)}
        onClose={handleCloseDrawer}
        width={520}
        extra={
          <Space>
            <Button
              icon={<ReloadOutlined />}
              onClick={handleRefreshSelectedReservation}
              loading={detailsLoading}
              disabled={!selectedReservation}
            >
              Rafraichir
            </Button>
            {selectedReservation && canEditReservations ? (
              <Button onClick={() => openEditReservation(selectedReservation)}>
                Modifier
              </Button>
            ) : null}
            {selectedReservation &&
            canEditReservations &&
            (selectedReservation.status === "confirmed" ||
              selectedReservation.status === "pending") ? (
              <Button
                onClick={() =>
                  handleQuickStatusUpdate(selectedReservation, "checked_in")
                }
              >
                Arrivee
              </Button>
            ) : null}
            {selectedReservation &&
            canEditReservations &&
            selectedReservation.status === "checked_in" ? (
              <Button
                onClick={() =>
                  handleQuickStatusUpdate(selectedReservation, "checked_out")
                }
              >
                Depart
              </Button>
            ) : null}
          </Space>
        }
      >
        {selectedReservation ? (
          <Space direction="vertical" size={16} style={{ width: "100%" }}>
            <Descriptions column={2} size="small">
              <Descriptions.Item label="Reservation">
                {selectedReservation.reservationNumber}
              </Descriptions.Item>
              <Descriptions.Item label="Client">
                {selectedReservation.guestName}
              </Descriptions.Item>
              <Descriptions.Item label="Arrivee">
                {dayjs(selectedReservation.arrivalDate).format("DD/MM/YYYY")}
              </Descriptions.Item>
              <Descriptions.Item label="Depart">
                {dayjs(selectedReservation.departureDate).format("DD/MM/YYYY")}
              </Descriptions.Item>
              <Descriptions.Item label="Clients">
                {selectedReservation.adults} adulte(s), {selectedReservation.children} enfant(s)
              </Descriptions.Item>
              <Descriptions.Item label="Propriete">
                {selectedReservation.propertyName}
              </Descriptions.Item>
              <Descriptions.Item label="Statut">
                <Tag color={statusColors[selectedReservation.status]}>
                  {statusLabels[selectedReservation.status]}
                </Tag>
              </Descriptions.Item>
              <Descriptions.Item label="Paiement">
                <Tag color={paymentColors[selectedReservation.paymentStatus]}>
                  {paymentLabels[selectedReservation.paymentStatus]}
                </Tag>
              </Descriptions.Item>
              <Descriptions.Item label="Total">
                {formatMoney(selectedReservation.totalAmount, selectedReservation.propertyId)}
              </Descriptions.Item>
              <Descriptions.Item label="Solde du">
                {formatMoney(selectedReservation.balanceDue, selectedReservation.propertyId)}
              </Descriptions.Item>
            </Descriptions>
            <Divider />
            <Descriptions column={1} size="small">
              <Descriptions.Item label="Source">
                {sourceLabels[selectedReservation.source]}
              </Descriptions.Item>
              {selectedReservation.channelReference ? (
                <Descriptions.Item label="Ref canal">
                  {selectedReservation.channelReference}
                </Descriptions.Item>
              ) : null}
              {selectedReservation.notes ? (
                <Descriptions.Item label="Notes">
                  {selectedReservation.notes}
                </Descriptions.Item>
              ) : null}
            </Descriptions>
            <Divider />
            <Space direction="vertical" size={8} style={{ width: "100%" }}>
              <Space
                align="center"
                style={{ justifyContent: "space-between", width: "100%" }}
              >
                <Space size={8}>
                  <Text strong>Chambres attribuees</Text>
                  {canEditReservations ? (
                    <Button size="small" onClick={openAssignmentModal}>
                      Assigner des chambres
                    </Button>
                  ) : null}
                </Space>
                {selectedReservation &&
                reservationRoomsUpdatedMap[selectedReservation.id] ? (
                  <Text type="secondary">
                    Mis a jour{" "}
                    {reservationRoomsUpdatedMap[selectedReservation.id].format(
                      "HH:mm:ss"
                    )}
                  </Text>
                ) : null}
              </Space>
              {selectedReservation &&
              reservationRoomsMap[selectedReservation.id]?.length ? (
                <Table
                  columns={roomColumns}
                  dataSource={reservationRoomsMap[selectedReservation.id]}
                  rowKey="id"
                  pagination={false}
                  size="small"
                  loading={detailsLoading}
                />
              ) : detailsLoading ? (
                <Card>
                  <Text type="secondary">Chargement des attributions...</Text>
                </Card>
              ) : (
                <Empty description="Aucune chambre attribuee." />
              )}
            </Space>
          </Space>
        ) : null}
      </Drawer>

      <Modal
        title="Nouvelle reservation"
        open={isCreateOpen}
        onCancel={() => setIsCreateOpen(false)}
        onOk={handleCreateReservation}
        okText="Creer"
      >
        <Form
          form={form}
          layout="vertical"
          initialValues={{
            adults: 1,
            children: 0,
            status: "pending",
            source: "walk_in",
            paymentStatus: "unpaid",
            totalAmount: 0,
            paidAmount: 0,
          }}
        >
            <Form.Item
              label="Propriete"
              name="propertyId"
              rules={[{ required: true, message: "Propriete requise" }]}
            >
            <Select
              options={properties.map((property) => ({
                value: property.id,
                label: property.name,
              }))}
            />
          </Form.Item>
            <Form.Item
              label="Client"
              name="guestId"
              rules={[{ required: true, message: "Client requis" }]}
            >
            <Select
              showSearch
              optionFilterProp="label"
              options={guests.map((guest) => ({
                value: guest.id,
                label: `${guest.first_name} ${guest.last_name}`,
              }))}
            />
          </Form.Item>
            <Form.Item
              label="Arrivee / Depart"
              name="dateRange"
              rules={[{ required: true, message: "Dates requises" }]}
            >
            <RangePicker />
          </Form.Item>
          <Row gutter={16}>
            <Col xs={12}>
              <Form.Item
                label="Adultes"
                name="adults"
                rules={[{ required: true }]}
              >
                <InputNumber min={1} max={8} style={{ width: "100%" }} />
              </Form.Item>
            </Col>
            <Col xs={12}>
              <Form.Item label="Enfants" name="children">
                <InputNumber min={0} max={6} style={{ width: "100%" }} />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col xs={12}>
              <Form.Item label="Statut" name="status">
                <Select
                  options={[
                    { value: "pending", label: statusLabels.pending },
                    { value: "confirmed", label: statusLabels.confirmed },
                    { value: "checked_in", label: statusLabels.checked_in },
                    { value: "checked_out", label: statusLabels.checked_out },
                    { value: "cancelled", label: statusLabels.cancelled },
                    { value: "no_show", label: statusLabels.no_show },
                  ]}
                />
              </Form.Item>
            </Col>
            <Col xs={12}>
              <Form.Item label="Source" name="source">
                <Select
                  options={[
                    { value: "walk_in", label: sourceLabels.walk_in },
                    { value: "phone", label: sourceLabels.phone },
                    { value: "email", label: sourceLabels.email },
                    { value: "website", label: sourceLabels.website },
                    { value: "ota", label: sourceLabels.ota },
                    { value: "corporate", label: sourceLabels.corporate },
                    { value: "agency", label: sourceLabels.agency },
                    { value: "group", label: sourceLabels.group },
                  ]}
                />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col xs={12}>
              <Form.Item label="Statut paiement" name="paymentStatus">
                <Select
                  options={[
                    { value: "unpaid", label: paymentLabels.unpaid },
                    { value: "partial", label: paymentLabels.partial },
                    { value: "paid", label: paymentLabels.paid },
                    { value: "refunded", label: paymentLabels.refunded },
                  ]}
                />
              </Form.Item>
            </Col>
            <Col xs={12}>
              <Form.Item label="Ref canal" name="channelReference">
                <Input placeholder="Optionnel" />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col xs={12}>
              <Form.Item label="Total" name="totalAmount">
                <InputNumber min={0} style={{ width: "100%" }} />
              </Form.Item>
            </Col>
            <Col xs={12}>
              <Form.Item label="Paye" name="paidAmount">
                <InputNumber min={0} style={{ width: "100%" }} />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item label="Notes" name="notes">
            <Input.TextArea rows={3} placeholder="Demandes speciales ou notes" />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title="Modifier reservation"
        open={isEditOpen}
        onCancel={() => {
          setIsEditOpen(false);
          setEditingReservation(null);
        }}
        onOk={handleUpdateReservation}
        okText="Enregistrer"
      >
        <Form form={editForm} layout="vertical">
          <Form.Item
            label="Arrivee / Depart"
            name="dateRange"
            rules={[{ required: true, message: "Dates requises" }]}
          >
            <RangePicker />
          </Form.Item>
          <Row gutter={16}>
            <Col xs={12}>
              <Form.Item
                label="Adultes"
                name="adults"
                rules={[{ required: true }]}
              >
                <InputNumber min={1} max={8} style={{ width: "100%" }} />
              </Form.Item>
            </Col>
            <Col xs={12}>
              <Form.Item label="Enfants" name="children">
                <InputNumber min={0} max={6} style={{ width: "100%" }} />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col xs={12}>
              <Form.Item label="Statut" name="status">
                <Select
                  options={[
                    { value: "pending", label: statusLabels.pending },
                    { value: "confirmed", label: statusLabels.confirmed },
                    { value: "checked_in", label: statusLabels.checked_in },
                    { value: "checked_out", label: statusLabels.checked_out },
                    { value: "cancelled", label: statusLabels.cancelled },
                    { value: "no_show", label: statusLabels.no_show },
                  ]}
                />
              </Form.Item>
            </Col>
            <Col xs={12}>
              <Form.Item label="Source" name="source">
                <Select
                  options={[
                    { value: "walk_in", label: sourceLabels.walk_in },
                    { value: "phone", label: sourceLabels.phone },
                    { value: "email", label: sourceLabels.email },
                    { value: "website", label: sourceLabels.website },
                    { value: "ota", label: sourceLabels.ota },
                    { value: "corporate", label: sourceLabels.corporate },
                    { value: "agency", label: sourceLabels.agency },
                    { value: "group", label: sourceLabels.group },
                  ]}
                />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col xs={12}>
              <Form.Item label="Statut paiement" name="paymentStatus">
                <Select
                  options={[
                    { value: "unpaid", label: paymentLabels.unpaid },
                    { value: "partial", label: paymentLabels.partial },
                    { value: "paid", label: paymentLabels.paid },
                    { value: "refunded", label: paymentLabels.refunded },
                  ]}
                />
              </Form.Item>
            </Col>
            <Col xs={12}>
              <Form.Item label="Ref canal" name="channelReference">
                <Input placeholder="Optionnel" />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col xs={12}>
              <Form.Item label="Total" name="totalAmount">
                <InputNumber min={0} style={{ width: "100%" }} />
              </Form.Item>
            </Col>
            <Col xs={12}>
              <Form.Item label="Paye" name="paidAmount">
                <InputNumber min={0} style={{ width: "100%" }} />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item label="Notes" name="notes">
            <Input.TextArea rows={3} placeholder="Demandes speciales ou notes" />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title="Assigner des chambres"
        open={isAssignOpen}
        onCancel={() => setIsAssignOpen(false)}
        onOk={handleSaveAssignments}
        okText="Enregistrer"
        confirmLoading={assignmentLoading}
        width={860}
        bodyStyle={{ maxHeight: "70vh", overflowY: "auto" }}
      >
        <Form form={assignmentForm} layout="vertical">
          <Row gutter={12}>
            <Col xs={24} md={12}>
              <Form.Item
                label="Plan tarifaire"
                name="ratePlanId"
                rules={
                  ratePlans.length > 0
                    ? [{ required: true, message: "Plan requis" }]
                    : []
                }
              >
                <Select
                  loading={loadingRatePlans}
                  disabled={loadingRatePlans || ratePlans.length === 0}
                  placeholder={
                    ratePlans.length === 0
                      ? "Aucun plan tarifaire"
                      : "Selectionner un plan"
                  }
                  options={ratePlans.map((plan) => ({
                    value: plan.id,
                    label: plan.name,
                  }))}
                />
              </Form.Item>
            </Col>
          </Row>
          <Alert
            showIcon
            type="info"
            style={{ marginBottom: 12 }}
            message={`Total estime: ${formatMoneyWithSuffix(
              estimatedTotal,
              estimateCurrency
            )}`}
            description={`Base sur ${selectedReservation?.nights ?? 0} nuit(s). ${estimateNote}`}
          />
          <Form.List name="rooms">
            {(fields, { add, remove }) => (
              <>
                {fields.map((field) => (
                  <Card
                    key={field.key}
                    size="small"
                    style={{ marginBottom: 12 }}
                  >
                    <Row gutter={12}>
                      <Col xs={24} md={10}>
                        <Form.Item
                          {...field}
                          label="Type de chambre"
                          name={[field.name, "roomTypeId"]}
                          rules={[
                            { required: true, message: "Type requis" },
                          ]}
                        >
                          <Select
                            options={roomTypes.map((roomType) => ({
                              value: roomType.id,
                              label: roomType.name,
                            }))}
                          />
                        </Form.Item>
                      </Col>
                      <Col xs={24} md={10}>
                        <Form.Item shouldUpdate noStyle>
                          {() => {
                            const selectedRoomTypeId = assignmentForm.getFieldValue([
                              "rooms",
                              field.name,
                              "roomTypeId",
                            ]);
                            const availableRooms = selectedRoomTypeId
                              ? rooms.filter(
                                  (room) =>
                                    Number(room.room_type_id) ===
                                    Number(selectedRoomTypeId)
                                )
                              : rooms;
                            return (
                              <Form.Item
                                label="Chambre"
                                name={[field.name, "roomId"]}
                              >
                                <Select
                                  allowClear
                                  placeholder="Attribution auto"
                                  options={availableRooms.map((room) => ({
                                    value: room.id,
                                    label: room.room_number,
                                  }))}
                                />
                              </Form.Item>
                            );
                          }}
                        </Form.Item>
                      </Col>
                      <Col xs={24} md={4}>
                        <Form.Item label="Statut" name={[field.name, "status"]}>
                          <Select
                            options={[
                              { value: "reserved", label: roomStatusLabels.reserved },
                              { value: "assigned", label: roomStatusLabels.assigned },
                              { value: "checked_in", label: roomStatusLabels.checked_in },
                              { value: "checked_out", label: roomStatusLabels.checked_out },
                              { value: "cancelled", label: roomStatusLabels.cancelled },
                              { value: "no_show", label: roomStatusLabels.no_show },
                            ]}
                          />
                        </Form.Item>
                      </Col>
                    </Row>
                    <Row gutter={12}>
                      <Col xs={24} md={8}>
                        <Form.Item label="Adultes" name={[field.name, "adults"]}>
                          <InputNumber min={1} max={8} style={{ width: "100%" }} />
                        </Form.Item>
                      </Col>
                      <Col xs={24} md={8}>
                        <Form.Item label="Enfants" name={[field.name, "children"]}>
                          <InputNumber min={0} max={6} style={{ width: "100%" }} />
                        </Form.Item>
                      </Col>
                      <Col xs={24} md={8}>
                        <Button
                          danger
                          type="link"
                          style={{ padding: 0 }}
                          onClick={() => remove(field.name)}
                        >
                          Retirer
                        </Button>
                      </Col>
                    </Row>
                  </Card>
                ))}
                <Button
                  type="dashed"
                  onClick={() =>
                    add({
                      status: "reserved",
                      adults: selectedReservation?.adults || 1,
                      children: selectedReservation?.children || 0,
                    })
                  }
                  block
                >
                Ajouter une chambre
                </Button>
              </>
            )}
          </Form.List>
        </Form>
      </Modal>
    </Space>
  );
};

export default ReservationsTab;
